import { fetchWithAuth } from './api';
import { OperationalSettings } from '../types/operationalSettings';

// Reads/writes the Operational View's per-user preferences (theme, layout, ...) on the
// user's server-side profile, so they follow the user across browsers/machines instead of
// being stuck in localStorage on a single device.
//
// The PUT is a partial update: the backend merges the keys it receives into the stored
// blob, so sending only what changed can never drop the keys this client didn't send.

const PREFERENCES_URL = '/api/v1/user/profile/preferences';

/**
 * Returns the stored preferences ({} when the user has never saved any), or null when the
 * backend could not be reached — callers must keep their current values in that case
 * rather than fall back to defaults.
 */
export const loadOperationalSettings = async (): Promise<Partial<OperationalSettings> | null> => {
  try {
    const response = await fetchWithAuth(PREFERENCES_URL);
    if (!response.ok) {
      console.warn('Failed to load operational settings: unexpected status', response.status);
      return null;
    }
    const data = await response.json();
    const stored = data?.operational_settings;
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
    return stored as Partial<OperationalSettings>;
  } catch (e) {
    console.warn('Failed to load operational settings:', e);
    return null;
  }
};

/**
 * Persists the given keys. An empty object resets the stored preferences, which is how
 * "restore defaults" is expressed.
 */
export const saveOperationalSettings = async (
  updates: Partial<OperationalSettings>
): Promise<boolean> => {
  try {
    const response = await fetchWithAuth(PREFERENCES_URL, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ operational_settings: updates }),
    });
    if (!response.ok) {
      console.warn('Failed to save operational settings: unexpected status', response.status);
      return false;
    }
    return true;
  } catch (e) {
    console.warn('Failed to save operational settings:', e);
    return false;
  }
};
